import { Box, Card, CardContent, Stack } from '@mui/material';
import { Navigate, useParams } from 'react-router-dom';

import { CircularProgress } from '@occc/ui-design-system';
import { NotFoundPage, OfferSupportAlert, PAGE_HEADER_HEIGHT } from '@occc-app/design-system';
import { useChainId } from '@occc-app/Chain/hooks';
import { useGetOfferById } from '@occc-app/dataAccessLayer/offer/queries';
import UnauthorizedPage from '@occc-app/routes/Unauthorized';
import { useGetIsUserPermittedTo } from '@occc/utils-access-control';

import { messages } from './messages';
import {
  getOfferEditRedirectFeatures,
  offerEditRedirectNavigationState,
  resolveOfferEditUrl,
} from './utils';
import { CampaignComponentTabs } from '../CampaignComponents/components/ConfigurationLayout';

const INITIAL_LOADER_HEIGHT = '100vh';

const isSupportedTab = (value?: string): value is CampaignComponentTabs =>
  Object.values(CampaignComponentTabs).includes(value as CampaignComponentTabs);

const UnsupportedOfferConfiguration = ({ offerId }: { offerId: string }) => (
  <Card
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: `${PAGE_HEADER_HEIGHT}px`,
      height: `${PAGE_HEADER_HEIGHT * 2}px`,
      maxWidth: {
        xs: '90vw',
        md: '60vw',
      },
      mx: 'auto',
    }}
  >
    <CardContent>
      <Stack justifyContent='center' alignItems='center'>
        <OfferSupportAlert
          title={messages.unsupportedConfiguration}
          fallbackLink={`/offers/${offerId}`}
          fallbackLinkTitle={messages.viewOriginalOffer}
        />
      </Stack>
    </CardContent>
  </Card>
);

export const OfferEditRedirectPage = () => {
  const chainId = useChainId();
  const { offerId = '', '*': tabPath } = useParams<{ offerId: string; '*': string }>();
  const isUserPermittedTo = useGetIsUserPermittedTo();

  const tabSegment = tabPath?.split('/')[0];
  const tab = isSupportedTab(tabSegment) ? tabSegment : CampaignComponentTabs.Configure;

  const { loading, error, data: offer } = useGetOfferById(offerId, chainId);

  if (loading) {
    return (
      <Box
        display='flex'
        alignItems='center'
        justifyContent='center'
        sx={{ height: INITIAL_LOADER_HEIGHT }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !offer) {
    return <NotFoundPage />;
  }

  const features = getOfferEditRedirectFeatures(offer);

  if (!features) {
    return <UnsupportedOfferConfiguration offerId={offer.id} />;
  }

  if (!features.some((feature) => isUserPermittedTo(feature))) {
    return <UnauthorizedPage />;
  }

  const editUrl = resolveOfferEditUrl(offer, tab);

  return <Navigate to={editUrl!} replace state={offerEditRedirectNavigationState} />;
};
